import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { PageHeader, StatCard } from "@/components/common/DashboardBits";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { ClipboardList, ExternalLink, ShieldCheck } from "lucide-react";
import { safeHttpUrl } from "@/lib/url";
import BankPendingCard from "@/components/bank/BankPendingCard";

export default function BankOverview() {
  const { t } = useLang();
  const { user } = useAuth();
  const stats = useDashboardStats();
  const [bank, setBank] = useState(null);
  const entityId = user?.entity_id;
  const approved = stats ? Boolean(stats.is_approved) : true;

  useEffect(() => {
    if (entityId) api.get(`/banks/${entityId}`).then((r) => setBank(r.data)).catch(() => {});
  }, [entityId]);

  return (
    <div>
      <PageHeader title={`${t("welcome_back")}, ${user?.name || ""}`} subtitle={bank ? `${bank.name} — ${bank.branch}` : ""}
        right={stats && approved ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-semibold" data-testid="bank-approved-badge"><ShieldCheck className="h-3.5 w-3.5" /> Approved partner</span> : null} />
      {stats && !approved && <BankPendingCard />}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <StatCard testId="stat-total" label={t("total_leads")} value={stats?.total_leads ?? "—"} />
        <StatCard testId="stat-pending" label={t("pending")} value={stats?.pending ?? 0} tone="amber" />
        <StatCard testId="stat-in-progress" label={t("in_progress")} value={stats?.in_progress ?? 0} tone="blue" />
        <StatCard testId="stat-visited" label={t("visited")} value={stats?.visited ?? 0} tone="green" />
        <StatCard testId="stat-submitted" label={t("submitted")} value={stats?.submitted ?? 0} tone="green" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-slate-200">
          <CardContent className="p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Quick actions</div>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to="/bank/leads" className="inline-flex items-center gap-2 px-4 py-2 bg-slate-950 hover:bg-slate-800 text-white rounded-md text-sm">
                <ClipboardList className="h-4 w-4" /> {t("my_leads")}
              </Link>
              {safeHttpUrl(bank?.portal_link) && (
                <a href={safeHttpUrl(bank.portal_link)} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 hover:bg-slate-50 rounded-md text-sm">
                  <ExternalLink className="h-4 w-4" /> {t("portal_link")}
                </a>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
